import React, { useEffect, useRef } from 'react';

const AuthBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = canvas.width = window.innerWidth;
    let height = canvas.height = window.innerHeight;

    // Brand Colors
    const PRIMARY_COLOR = '#0e444e'; // Deep Teal
    const ACCENT_COLOR = '#d5b481'; // Gold
    
    // --- Background Pattern Particles ---
    interface PatternParticle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      angle: number;
      spinSpeed: number;
      type: 'square' | 'cross' | 'circle';
    }

    const patternParticles: PatternParticle[] = [];
    const patternCount = Math.min(Math.floor((width * height) / 20000), 60); 

    const createPatternParticle = (): PatternParticle => ({
      x: Math.random() * width,
      y: Math.random() * height,
      size: Math.random() * 15 + 5,
      speedX: (Math.random() - 0.5) * 0.3,
      speedY: (Math.random() - 0.5) * 0.3,
      angle: Math.random() * Math.PI * 2,
      spinSpeed: (Math.random() - 0.5) * 0.01,
      type: Math.random() > 0.6 ? 'cross' : (Math.random() > 0.5 ? 'square' : 'circle'),
    });

    for (let i = 0; i < patternCount; i++) {
      patternParticles.push(createPatternParticle());
    }

    // --- Mouse Dust Particles ---
    interface DustParticle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      life: number;     // 0 to 1
      decay: number;    // How fast it fades
      color: string;
    }

    let dustParticles: DustParticle[] = [];

    const createDust = (x: number, y: number) => {
      const count = 3; // Number of particles per event
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 1.5 + 0.5;
        dustParticles.push({
          x,
          y,
          size: Math.random() * 2 + 0.5,
          speedX: Math.cos(angle) * speed,
          speedY: Math.sin(angle) * speed,
          life: 1.0,
          decay: Math.random() * 0.02 + 0.01,
          color: ACCENT_COLOR
        });
      }
    };

    // --- Drawing Functions ---

    const drawPattern = (p: PatternParticle) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.strokeStyle = ACCENT_COLOR;
      ctx.fillStyle = ACCENT_COLOR;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.1; // Very subtle

      if (p.type === 'cross') {
        const s = p.size;
        ctx.beginPath();
        ctx.moveTo(-s, 0); ctx.lineTo(s, 0);
        ctx.moveTo(0, -s); ctx.lineTo(0, s);
        ctx.stroke();
      } else if (p.type === 'square') {
        ctx.strokeRect(-p.size/2, -p.size/2, p.size, p.size);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.size/4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    const drawConnections = () => {
      ctx.lineWidth = 0.5;
      patternParticles.forEach((p1, i) => {
        // Connect to other particles
        patternParticles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.strokeStyle = ACCENT_COLOR;
            ctx.globalAlpha = (1 - dist / 120) * 0.08; 
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });

        // Connect to mouse (interactive web)
        const dx = p1.x - mouseRef.current.x;
        const dy = p1.y - mouseRef.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
            ctx.beginPath();
            ctx.strokeStyle = ACCENT_COLOR;
            ctx.globalAlpha = (1 - dist / 200) * 0.15;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
            ctx.stroke();
        }
      });
    };

    const drawDust = () => {
      for (let i = dustParticles.length - 1; i >= 0; i--) {
        const p = dustParticles[i];
        
        // Update
        p.x += p.speedX;
        p.y += p.speedY;
        p.life -= p.decay;

        // Remove dead particles
        if (p.life <= 0) {
          dustParticles.splice(i, 1);
          continue;
        }

        // Draw
        ctx.save();
        ctx.globalAlpha = p.life * 0.8;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Optional: Glow effect
        ctx.shadowBlur = 5;
        ctx.shadowColor = p.color;
        ctx.restore();
      }
    };

    // --- Animation Loop ---
    let animationId: number;

    const animate = () => {
      // Clear with trail effect? No, clean clear for crisp dust
      ctx.fillStyle = PRIMARY_COLOR;
      ctx.fillRect(0, 0, width, height);

      // Vignette / Gradient
      const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, width);
      gradient.addColorStop(0, 'rgba(14, 68, 78, 0)');
      gradient.addColorStop(1, 'rgba(8, 51, 56, 0.8)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // 1. Draw Background Pattern
      patternParticles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.angle += p.spinSpeed;

        // Wrap
        if (p.x < -50) p.x = width + 50;
        if (p.x > width + 50) p.x = -50;
        if (p.y < -50) p.y = height + 50;
        if (p.y > height + 50) p.y = -50;

        drawPattern(p);
      });

      drawConnections();

      // 2. Draw Mouse Dust
      drawDust();

      animationId = requestAnimationFrame(animate);
    };

    // --- Event Listeners ---
    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      createDust(e.clientX, e.clientY);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full -z-10" />;
};

export default AuthBackground;
